import * as React from 'react';
import {
  Routes,
  Route,
  useLocation,
  Navigate,
  unstable_HistoryRouter as HistoryRouter
} from 'react-router-dom';
// import { useSelector } from 'react-redux';
import { history } from './app/history';
import { getLoggedInUser } from 'utilities/helpers';
import Login from './pages/login';
import { MsalProvider } from '@azure/msal-react';
import { EventType, PublicClientApplication } from '@azure/msal-browser';
import { useSelector } from 'react-redux';
// import LocalStorage from 'utilities/localStorage';
import Dashboard from './pages/Dashboard';
import Tasks from './pages/Tasks';
import EmployeeTasks from './pages/EmployeeTasks';
import AllEmployeeTasks from './pages/AllEmployeeTasks';
import { Skeleton } from 'antd';
import { EMPLOYEE, MANAGER, EXECUTIVE } from 'utilities/roleRights';
import FollowUp from 'pages/FollowUp';
import FollowUpTablePage from 'pages/FollowUp/components/FollowUpTablePage';
import Configure from 'pages/Configure';
import Users from 'pages/Configure/pages/Users';
import Clients from 'pages/Configure/pages/Clients';
import Projects from 'pages/Configure/pages/Projects';
import PublishPlan from 'pages/Tasks/pages/PublishPlan';
import Plans from 'pages/Tasks/pages/Plans';
import ViewPlan from 'pages/Tasks/pages/ViewPlan';
import Staffing from 'pages/Staffing';
import PlanStaffing from 'pages/PlanStaffing';
import { PCMAudioRecorderProvider } from '@speechmatics/browser-audio-input-react';
import { RealtimeTranscriptionProvider } from '@speechmatics/real-time-client-react';
import { SAMPLE_RATE } from 'pages/FloatingActionButton';
import WeeklyList from 'pages/WeeklyList';
// import FloatingActionButton from './pages/FloatingActionButton';

const configuration = {
  auth: {
    clientId:
      process.env.REACT_APP_CLIENT_ID || '7bc704f7-4434-406e-97d1-ae6fd7d0481b',
    authority:
      process.env.REACT_APP_AUTHORITY ||
      'https://login.microsoftonline.com/07686590-1316-4824-bf97-d3e8c1a0d547',
    redirectUri:
      process.env.REACT_APP_URL || 'https://app.trackex.co/session.html'
    // process.env.REACT_APP_URL || 'https://dev.trackex.co/session.html'
  },
  cache: {
    cacheLocation: 'localStorage', // This configures where your cache will be stored
    storeAuthStateInCookie: false // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    windowHashTimeout: 60000,
    iframeHashTimeout: 60000,
    loadFrameTimeout: 0,
    asyncPopups: false
  }
};

const pca = new PublicClientApplication(configuration);

pca.addEventCallback((event) => {
  if (event.eventType === EventType.LOGIN_SUCCESS) {
    pca.setActiveAccount(event.payload.account);
  }
});

const PrivateRoute = ({ children }) => {
  let location = useLocation();

  const { loading } = useSelector((state) => state.login);
  const { loggedIn } = getLoggedInUser();

  if (loading) {
    return <Skeleton />;
  }

  if (!loggedIn) {
    return <Navigate to="/login" state={{ from: location }} />;
  }

  return children;
};

const RoleBasedOnlyRoute = ({ role = [], children }) => {
  const { user_details } = getLoggedInUser();
  const hasAccess = role.some((r) => r === user_details?.role_id);

  if (hasAccess) {
    return children;
  } else {
    return <Navigate to="/dashboard" replace />;
  }
};

const AuthChecker = ({ children }) => {
  let location = useLocation();
  const { loggedIn } = getLoggedInUser();
  if (loggedIn) {
    return <Navigate to={'/'} state={{ from: location }} />;
  }

  return children;
};

function App() {
  const user = getLoggedInUser();
  const audioContext = new AudioContext({ sampleRate: SAMPLE_RATE });
  return (
    <RealtimeTranscriptionProvider appId="default">
      <PCMAudioRecorderProvider
        audioContext={audioContext}
        workletScriptURL="/js/pcm-audio-worklet.min.js"
      >
        <HistoryRouter history={history}>
          <Routes>
            <Route
              path="login"
              element={
                <AuthChecker>
                  <MsalProvider instance={pca}>
                    <Login />
                  </MsalProvider>
                </AuthChecker>
              }
            />
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <MsalProvider instance={pca}>
                    <Dashboard />
                  </MsalProvider>
                </PrivateRoute>
              }
            />
            <Route
              path="dashboard"
              element={
                <PrivateRoute>
                  <MsalProvider instance={pca}>
                    <Dashboard />
                  </MsalProvider>
                </PrivateRoute>
              }
            />
            <Route
              path="tasks"
              element={
                <PrivateRoute>
                  <MsalProvider instance={pca}>
                    <RoleBasedOnlyRoute role={[MANAGER, EXECUTIVE]}>
                      <Tasks />
                    </RoleBasedOnlyRoute>
                  </MsalProvider>
                </PrivateRoute>
              }
            />
            <Route
              path="publish-plan"
              element={
                <PrivateRoute>
                  <MsalProvider instance={pca}>
                    <RoleBasedOnlyRoute role={[MANAGER, EXECUTIVE]}>
                      <PublishPlan />
                    </RoleBasedOnlyRoute>
                  </MsalProvider>
                </PrivateRoute>
              }
            />
            <Route
              path="plans"
              element={
                <PrivateRoute>
                  <MsalProvider instance={pca}>
                    <RoleBasedOnlyRoute role={[MANAGER, EXECUTIVE]}>
                      <Plans />
                    </RoleBasedOnlyRoute>
                  </MsalProvider>
                </PrivateRoute>
              }
            />
            <Route
              path="view-plan"
              element={
                <PrivateRoute>
                  <MsalProvider instance={pca}>
                    <RoleBasedOnlyRoute role={[MANAGER, EXECUTIVE]}>
                      <ViewPlan />
                    </RoleBasedOnlyRoute>
                  </MsalProvider>
                </PrivateRoute>
              }
            />
            <Route
              path="my-tasks"
              element={
                <PrivateRoute>
                  <MsalProvider instance={pca}>
                    <RoleBasedOnlyRoute role={[MANAGER, EMPLOYEE, EXECUTIVE]}>
                      <EmployeeTasks />
                    </RoleBasedOnlyRoute>
                  </MsalProvider>
                </PrivateRoute>
              }
            />
            <Route
              path="all-tasks"
              element={
                <PrivateRoute>
                  <MsalProvider instance={pca}>
                    <RoleBasedOnlyRoute role={[MANAGER, EMPLOYEE, EXECUTIVE]}>
                      <AllEmployeeTasks />
                    </RoleBasedOnlyRoute>
                  </MsalProvider>
                </PrivateRoute>
              }
            />
            <Route
              path="manage-staffing"
              element={
                <PrivateRoute>
                  <MsalProvider instance={pca}>
                    <RoleBasedOnlyRoute role={[EXECUTIVE]}>
                      <Staffing />
                    </RoleBasedOnlyRoute>
                  </MsalProvider>
                </PrivateRoute>
              }
            />

            <Route
              path="plan-staffing"
              element={
                <PrivateRoute>
                  <MsalProvider instance={pca}>
                    <RoleBasedOnlyRoute role={[MANAGER, EXECUTIVE]}>
                      <PlanStaffing />
                    </RoleBasedOnlyRoute>
                  </MsalProvider>
                </PrivateRoute>
              }
            />

            <Route
              path="weekly-list"
              element={
                <PrivateRoute>
                  <MsalProvider instance={pca}>
                    <RoleBasedOnlyRoute role={[EXECUTIVE]}>
                      <WeeklyList />
                    </RoleBasedOnlyRoute>
                  </MsalProvider>
                </PrivateRoute>
              }
            />

            {/* ...................followup routes.................................. */}
            <Route
              path="follow-up"
              element={
                <PrivateRoute>
                  <MsalProvider instance={pca}>
                    <RoleBasedOnlyRoute role={[MANAGER, EXECUTIVE]}>
                      <FollowUp />
                    </RoleBasedOnlyRoute>
                  </MsalProvider>
                </PrivateRoute>
              }
            />
            <Route
              path="follow-up/all"
              element={
                <PrivateRoute>
                  <MsalProvider instance={pca}>
                    <RoleBasedOnlyRoute role={[MANAGER, EXECUTIVE]}>
                      <FollowUpTablePage />
                    </RoleBasedOnlyRoute>
                  </MsalProvider>
                </PrivateRoute>
              }
            />

            {/* ...................configure routes.................................. */}
            <Route
              path="configure"
              element={
                <PrivateRoute>
                  <MsalProvider instance={pca}>
                    <RoleBasedOnlyRoute role={[MANAGER, EXECUTIVE]}>
                      <Configure />
                    </RoleBasedOnlyRoute>
                  </MsalProvider>
                </PrivateRoute>
              }
            />
            <Route
              path="configure/users"
              element={
                <PrivateRoute>
                  <MsalProvider instance={pca}>
                    <RoleBasedOnlyRoute role={[MANAGER, EXECUTIVE]}>
                      <Users />
                    </RoleBasedOnlyRoute>
                  </MsalProvider>
                </PrivateRoute>
              }
            />
            <Route
              path="configure/clients"
              element={
                <PrivateRoute>
                  <MsalProvider instance={pca}>
                    <RoleBasedOnlyRoute role={[MANAGER, EXECUTIVE]}>
                      <Clients />
                    </RoleBasedOnlyRoute>
                  </MsalProvider>
                </PrivateRoute>
              }
            />
            <Route
              path="configure/projects"
              element={
                <PrivateRoute>
                  <MsalProvider instance={pca}>
                    <RoleBasedOnlyRoute role={[MANAGER, EXECUTIVE]}>
                      <Projects />
                    </RoleBasedOnlyRoute>
                  </MsalProvider>
                </PrivateRoute>
              }
            />
          </Routes>
          {/* {user?.user_details?.role === 1 ? <FloatingActionButton /> : ''} */}
        </HistoryRouter>
      </PCMAudioRecorderProvider>
    </RealtimeTranscriptionProvider>
  );
}

export default App;
